import { combineLatest, Observable, of, Subject, throwError } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  pairwise,
  switchMapTo,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';
import sha256 from 'crypto-js/sha256';

import { TokenService } from './token.service';
import { AccountInfoService } from './account-info.service';
import { Token } from '../models/identity.model';
import { select, Store } from '@ngrx/store';
import { getUserData } from '~shared/modules/user-info/store';
import { UserTypeUtil } from '~shared/utils/user-type.util';
import { IUserTypeLogInInfo } from '~shared/models/user-type-login-info';
import { AccountInfoFetchingStatus, AccountType, IAccountInformation } from '~shared/models';
import { NOT_AUTHORIZED } from '../constants/identity.constant';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { LoggerService } from '~shared/modules/logger/services/logger.service';
import { LoggerStrategies } from '~shared/modules/logger/models';
// prettier-ignore
import {
  AuthorizationStateType,
  FF_LOGIN_MONITORING_LOGGING_STRATEGY,
} from '~app/modules/authorization/constants/authorization.constant';
import { OAuthErrorEvent, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { NUMBER_1000 } from '~shared/constants';
import { environment } from '~env/environment';
import { isSoftLoggedIn } from '~server/utils/auth.utils';

@Injectable({
  providedIn: 'root',
})
export class IdentityService implements OnDestroy {
  public readonly isAuthorized$: Observable<boolean>;
  public readonly isEmployeeAuthorized$: Observable<boolean>;
  private unsubscribe$ = new Subject<void>();
  private loggedIn: boolean;

  public userData: IUserTypeLogInInfo;

  constructor(
    private accountInfoService: AccountInfoService,
    private tokenService: TokenService,
    private oauthService: OAuthService,
    private store$: Store<IAccountInformation>,
    private activeFeatureToggleService: ActiveFeaturesConfigcatService,
    private loggerService: LoggerService,
    private httpClient: HttpClient,
    private oAuthStorage: OAuthStorage
  ) {
    this.isAuthorized$ = this.isAuthorized();
    this.isEmployeeAuthorized$ = this.isEmployeeAuthorized();
    this.isAuthorized$
      .pipe(
        pairwise(),
        filter(([prevState, currentState]) => prevState && !currentState),
        switchMapTo(
          this.activeFeatureToggleService.getFeatureValue(
            FF_LOGIN_MONITORING_LOGGING_STRATEGY,
            LoggerStrategies.instanaLogger
          )
        ),
        takeUntil(
          this.oauthService.events.pipe(
            filter(
              (e) =>
                !isSoftLoggedIn() &&
                (e instanceof OAuthErrorEvent || ['logout', 'silent_refresh_error'].includes(e.type))
            ),
            take(1)
          )
        )
      )
      .subscribe((loggerStrategy) => {
        this.loggerService.error({}, `User auth state was changed to unauthorized`, loggerStrategy);
      });
  }

  public authorize(token: Token): Observable<any> {
    if (token) {
      this.tokenService.setToken(token);
    }
    return this.accountInfoService.fetchAccountInfo().pipe(
      catchError((error) => {
        this.unauthorize();
        return throwError(error);
      })
    );
  }

  private fetchTokenFromCode(code: string, redirectUri: string): Observable<Token> {
    const params = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('code', code)
      .set('redirect_uri', redirectUri);

    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    const baseHrefWithoutTrailingSlash = environment.baseHref().replace(new RegExp('/$'), '');

    return this.httpClient.post<Token>(`${baseHrefWithoutTrailingSlash}/magicLinkAuth`, params, { headers });
  }

  public authorizeWithCode(code: string, redirectUri: string): Observable<Token> {
    return this.fetchTokenFromCode(code, redirectUri).pipe(
      tap((token) => {
        if (token?.access_token) {
          this.tokenService.setToken(token);
          this.oAuthStorage.setItem('access_token', token.access_token);
          this.oAuthStorage.setItem('refresh_token', token.refresh_token);
          this.oAuthStorage.setItem('token_type', token.token_type);

          const expiresInMilliSeconds = token.expires_in * NUMBER_1000;
          const now = new Date();
          const expiresAt = now.getTime() + expiresInMilliSeconds;
          this.oAuthStorage.setItem('expires_at', `${expiresAt}`);
        }
      })
    );
  }

  public unauthorize(): void {
    this.tokenService.removeToken();
    this.accountInfoService.removeAccountInfo();
    this.accountInfoService.removeEmployeeAccountInfo();
  }

  public syncAccountInfo(): void {
    this.accountInfoService.fetchAccountInfo().pipe(takeUntil(this.unsubscribe$)).subscribe();
  }

  public isLoggedIn(): Observable<boolean> {
    return this.isAuthorized$;
  }

  public getUserInformation() {
    const isLoggedIn$ = this.isLoggedIn();

    isLoggedIn$.pipe(takeUntil(this.unsubscribe$)).subscribe((loginData) => {
      this.loggedIn = loginData;
    });

    this.store$.pipe(select(getUserData), takeUntil(this.unsubscribe$)).subscribe((userData) => {
      const registrationType = userData != null ? UserTypeUtil.getUserType(userData.registrationType) : NOT_AUTHORIZED;
      const loggedIn = userData != null ? this.loggedIn : false;
      const hashedIds = {
        hashedIdamUserId: userData?.hashedIdamUserId,
        hashedActiveContextId: userData?.hashedActiveContextId,
        hashedPersonCdmId: userData?.hashedPersonCdmId,
        hashedEmail: sha256(userData?.email || '').toString(),
      };
      this.userData = {
        registrationType,
        loggedIn,
        ...(userData && hashedIds),
      };
    });

    return this.userData;
  }

  public getAuthAccountInfoStatus(): Observable<
    AccountInfoFetchingStatus | AccountType | AuthorizationStateType.LoggedOut
  > {
    return this.tokenService.hasToken().pipe(
      mergeMap((hasToken) => {
        if (hasToken) {
          return this.accountInfoService.accountInfoFetchingStatus$;
        }

        return of<AuthorizationStateType.LoggedOut>(AuthorizationStateType.LoggedOut);
      })
    );
  }

  private isAuthorized(): Observable<boolean> {
    const accountInfo$ = this.accountInfoService.getAccountInfo();
    const hasToken$ = this.tokenService.hasToken();
    return combineLatest([hasToken$, accountInfo$]).pipe(
      map((values) => values.every(Boolean)),
      distinctUntilChanged()
    );
  }

  private isEmployeeAuthorized(): Observable<boolean> {
    const employeeAccountInfo$ = this.accountInfoService.getEmployeeAccountInfo();
    const hasToken$ = this.tokenService.hasToken();
    return combineLatest([hasToken$, employeeAccountInfo$]).pipe(
      map((values) => values.every(Boolean)),
      distinctUntilChanged()
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
